/**
 * Custom API error class with structured error information
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static fromSupabaseError(error: { message: string; code?: string; details?: unknown }): ApiError {
    return new ApiError(
      error.message || 'An unexpected error occurred',
      error.code || 'UNKNOWN_ERROR',
      undefined,
      error.details
    );
  }

  static networkError(): ApiError {
    return new ApiError(
      'Network error. Please check your connection.',
      'NETWORK_ERROR'
    );
  }

  static unauthorized(): ApiError {
    return new ApiError(
      'You must be logged in to perform this action.',
      'UNAUTHORIZED',
      401
    );
  }

  static forbidden(): ApiError {
    return new ApiError(
      'You do not have permission to perform this action.',
      'FORBIDDEN',
      403
    );
  }

  static notFound(resource = 'Resource'): ApiError {
    return new ApiError(
      `${resource} not found.`,
      'NOT_FOUND',
      404
    );
  }
}

/**
 * Result type for API operations - either success with data or error
 */
export type ApiResult<T> = 
  | { success: true; data: T }
  | { success: false; error: ApiError };

/**
 * Helper to create a success result
 */
export function ok<T>(data: T): ApiResult<T> {
  return { success: true, data };
}

/**
 * Helper to create an error result
 */
export function err<T>(error: ApiError): ApiResult<T> {
  return { success: false, error };
}
